// A procedural catalog for each country — deterministic per ISO code, so the
// same country always shows the same content.
//
// ⚠⚠ EVERY TITLE, GENRE, BADGE AND COUNT BELOW IS INVENTED. There is no real
// catalog, no data source and no client. The file was called `netflix.ts` and
// carried a "SAMBA PICK" badge, which put two real companies' names on
// fabricated data — the one thing a portfolio piece must never do. It is named
// for what it is now, and the page states the same thing where a reader sees it
// rather than only here.

const TITLES = [
  "Neon Meridian",
  "The Silver Tide",
  "Paper Horizons",
  "Static Bloom",
  "Long Autumn",
  "Aurora Protocol",
  "Glass City",
  "Undertow",
  "Last Frequency",
  "Vantage Point",
  "Midnight Transit",
  "Cold Open",
  "Parallel Lines",
  "Blackbird",
  "Echo Chamber",
  "Counterweight",
  "Signal Decay",
  "Orbit",
  "Anchor Point",
  "The Narrow Road",
  "Shoreline",
  "Breakwater",
  "Low Voltage",
  "Shadow Atlas",
  "North of Eden",
  "Deep Field",
  "Quiet Harbor",
  "Refraction",
  "The Observer",
  "Blind Spot",
  "Half Light",
  "The Long Thaw",
  "Still Waters",
  "Canopy",
  "Driftwood",
  "Riptide",
  "Wayfinder",
  "The Cartographer",
  "Terminal Velocity",
  "Backlit",
  "Slow Radio",
  "Red Meridian",
  "Open Circuit",
  "Crossroads",
  "Shortwave",
  "Interference",
  "The Archivist",
  "Silent Partner",
  "Undercurrent",
  "Lighthouse",
];

const GENRES = [
  "Drama",
  "Thriller",
  "Sci-Fi",
  "Docuseries",
  "Limited Series",
  "Comedy",
  "Mystery",
  "Crime",
];

const BADGES = ["TOP 10", "NEW", "EMMY", "EXCLUSIVE", "PICK"];

export interface Show {
  id: string;
  title: string;
  genre: string;
  year: number;
  match: number; // 70..99
  episodes: number;
  duration: string; // "50m" or "1h 20m"
  badge?: string;
  gradient: string;
}

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// Mulberry32 — seeded PRNG, deterministic per seed.
function makeRng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function gradientFor(seed: number): string {
  const hue1 = seed % 360;
  const hue2 = (hue1 + 30 + ((seed >> 4) % 60)) % 360;
  const sat = 28 + ((seed >> 8) % 42);
  const lum1 = 22 + ((seed >> 12) % 18);
  const lum2 = 8 + ((seed >> 14) % 10);
  return `linear-gradient(140deg, hsl(${hue1} ${sat}% ${lum1}%) 0%, hsl(${hue2} ${sat}% ${lum2}%) 100%)`;
}

function buildShow(title: string, rng: () => number): Show {
  const seed = hashStr(title);
  const rand = rng();
  return {
    id: title.toLowerCase().replace(/\s+/g, "-"),
    title,
    genre: GENRES[Math.floor(rand * GENRES.length)],
    year: 2019 + Math.floor(rng() * 6),
    match: 72 + Math.floor(rng() * 27),
    episodes: 4 + Math.floor(rng() * 20),
    duration:
      rng() > 0.5
        ? `${40 + Math.floor(rng() * 20)}m`
        : `1h ${Math.floor(rng() * 30)}m`,
    badge: rng() > 0.65 ? BADGES[Math.floor(rng() * BADGES.length)] : undefined,
    gradient: gradientFor(seed ^ Math.floor(rng() * 1e9)),
  };
}

export interface CountryCatalog {
  featured: Show;
  top10: Show[];
  trending: Show[];
  popular: Show[];
  becauseYouWatched: Show[];
}

export function catalogFor(code: string): CountryCatalog {
  const seed = hashStr(code || "XX");
  const rng = makeRng(seed);
  // Fisher-Yates with seeded RNG
  const pool = TITLES.slice();
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  const take = (n: number, offset: number) =>
    pool.slice(offset, offset + n).map((t) => buildShow(t, rng));

  const top10 = take(10, 0).map((s, i) => ({
    ...s,
    badge: i < 3 ? "TOP 10" : s.badge,
  }));
  const trending = take(8, 10);
  const popular = take(8, 18);
  const becauseYouWatched = take(6, 26);

  const featured = { ...top10[0], badge: "#1 IN COUNTRY" };

  return { featured, top10, trending, popular, becauseYouWatched };
}
